import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { saveCampaign } from "../../actions/ads";
import { Toggle } from "../settings/AlertCard";
import { fieldClass, labelClass, primaryButton, secondaryButton, smallButton } from "../template/templateLabels";
import { MAX_HOUR_RANGES, MAX_WEIGHT, WEEKDAYS, deliveryOf } from "./adLabels";

// Adds a campaign or edits one: its name, who pays for it, the days it runs, the
// hours and days of the week it plays, how often it comes up, on every kiosk or on
// some, and the kiosks it plays on. Its videos and images are added on its card once
// it is saved. The kiosks hold to the days and hours on their own, so a campaign can
// be saved, and turned on, before it starts.

// What the backend takes (utils/adCampaigns.js readCampaign), as a campaign has it
export const bodyOf = (campaign) => ({
	name: campaign.name,
	advertiser: campaign.advertiser,
	startDay: campaign.startDay,
	endDay: campaign.endDay,
	schedule: campaign.schedule,
	weight: campaign.weight,
	weights: campaign.weights,
	printers: campaign.printers,
	enabled: campaign.enabled,
});

const WEIGHTS = Array.from({ length: MAX_WEIGHT }, (_, index) => index + 1);
const ALL_DAYS = WEEKDAYS.map((day) => day.value);
const WORKDAYS = [1, 2, 3, 4, 5];

// A time field cannot show 24:00, the end of a day; it shows 00:00 for it
const fieldTime = (time) => (time === "24:00" ? "00:00" : time);
const bodyTime = (time) => (time === "00:00" ? "24:00" : time);

// The form's own: every day picked, and the kiosks' weights by id
const stateOf = (campaign) => ({
	name: campaign ? campaign.name : "",
	advertiser: campaign ? campaign.advertiser : "",
	startDay: campaign ? campaign.startDay : "",
	endDay: campaign ? campaign.endDay : "",
	days: campaign && campaign.schedule.days.length ? [...campaign.schedule.days] : ALL_DAYS,
	hours: campaign
		? campaign.schedule.hours.map(({ from, to }) => ({ from: fieldTime(from), to: fieldTime(to) }))
		: [],
	weight: campaign ? campaign.weight : 1,
	weights: campaign ? Object.fromEntries(campaign.weights.map(({ printer, weight }) => [printer, weight])) : {},
	printers: campaign ? [...campaign.printers] : [],
	enabled: campaign ? campaign.enabled : false,
});

const weightLabel = (weight) => (weight === 1 ? "1 (энгийн)" : `${weight} дахин`);

const radioClass = "text-purple-600 focus:ring-purple-200";
const chipClass = (on) =>
	"h-9 px-3 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 " +
	(on
		? "text-white bg-purple-600"
		: "text-gray-600 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600");
const timeClass =
	"h-10 px-3 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:border-purple-400 focus:ring focus:ring-purple-200 focus:ring-opacity-50";
const removeButton =
	"flex items-center justify-center flex-shrink-0 w-10 h-10 text-gray-400 rounded-lg hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200";

const CampaignForm = ({ campaign, printers, onSaved, onClose, saveCampaign }) => {
	const isNew = !campaign;
	const [form, setForm] = useState(stateOf(campaign));
	// Every kiosk, or the ones picked
	const [everywhere, setEverywhere] = useState(isNew || campaign.printers.length === 0);
	const [search, setSearch] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const set = (field, value) => setForm({ ...form, [field]: value });
	const picked = (id) => everywhere || form.printers.includes(id);
	const toggle = (id) =>
		set("printers", form.printers.includes(id) ? form.printers.filter((item) => item !== id) : [...form.printers, id]);
	const setKioskWeight = (id, value) => {
		const weights = { ...form.weights };
		if (value === "") delete weights[id];
		else weights[id] = Number(value);
		set("weights", weights);
	};

	const allDay = form.hours.length === 0;
	const setAllDay = (on) => set("hours", on ? [] : [{ from: "08:00", to: "12:00" }]);
	const setHour = (index, field, value) =>
		set(
			"hours",
			form.hours.map((range, i) => (i === index ? { ...range, [field]: value } : range)),
		);
	const toggleDay = (value) =>
		set("days", form.days.includes(value) ? form.days.filter((day) => day !== value) : [...form.days, value]);
	const sameDays = (days) => days.length === form.days.length && days.every((day) => form.days.includes(day));

	const term = search.trim().toLowerCase();
	const shown = printers.filter(
		(printer) => term === "" || `${printer.name} ${printer.location}`.toLowerCase().includes(term),
	);
	// A kiosk picked before that is not in the system any more
	const missing = form.printers.filter((id) => !printers.some((printer) => printer._id === id));
	// Kiosks it plays on with a weight other than the campaign's
	const ownWeights = Object.entries(form.weights)
		.filter(([id, weight]) => weight !== form.weight && picked(id) && printers.some((printer) => printer._id === id))
		.map(([printer, weight]) => ({ printer, weight }));

	const badHours = form.hours.some(
		({ from, to }) => !from || !to || from >= bodyTime(to),
	);

	const problem = !form.name.trim()
		? "Нэр бичнэ үү"
		: form.startDay && form.endDay && form.endDay < form.startDay
		? "Дуусах огноо эхлэх огнооноос өмнө байна"
		: badHours
		? "Цагийн завсар бүрт эхлэх цаг дуусах цагаасаа өмнө байна"
		: form.days.length === 0
		? "Дор хаяж нэг гараг сонгоно уу"
		: !everywhere && form.printers.length === 0
		? "Киоск сонгоно уу"
		: !everywhere && missing.length > 0
		? "Сонгосон киоскуудын зарим нь системээс устгагдсан байна"
		: null;
	const canSubmit = !submitting && !problem;

	const submit = async (e) => {
		e.preventDefault();
		if (!canSubmit) return;
		setSubmitting(true);
		const saved = await saveCampaign(isNew ? null : campaign._id, {
			name: form.name.trim(),
			advertiser: form.advertiser.trim(),
			startDay: form.startDay,
			endDay: form.endDay,
			schedule: {
				days: form.days.length === ALL_DAYS.length ? [] : form.days,
				hours: form.hours.map(({ from, to }) => ({ from, to: bodyTime(to) })),
			},
			weight: form.weight,
			weights: ownWeights,
			printers: everywhere ? [] : form.printers,
			enabled: form.enabled,
		});
		if (saved) {
			onSaved(saved);
			onClose();
		} else {
			setSubmitting(false);
		}
	};

	const close = () => {
		if (!submitting) onClose();
	};

	return (
		// Only a press on the backdrop itself closes the sheet
		<div
			className="fixed inset-0 z-30 flex items-end pt-12 bg-black bg-opacity-50 sm:items-center sm:justify-center sm:p-4"
			onMouseDown={(e) => {
				if (e.target === e.currentTarget) close();
			}}
		>
			<form
				onSubmit={submit}
				className="flex flex-col w-full max-h-full overflow-hidden bg-white rounded-t-lg dark:bg-gray-800 sm:rounded-lg sm:max-w-2xl"
			>
				<header className="flex items-start justify-between flex-shrink-0 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
					<div className="min-w-0">
						<p className="text-lg font-semibold text-gray-700 truncate dark:text-gray-300">
							{isNew ? "Шинэ кампанит ажил" : "Кампанит ажил засах"}
						</p>
						{!isNew && <p className="text-sm text-gray-600 truncate dark:text-gray-400">{campaign.name}</p>}
					</div>
					<button
						type="button"
						onClick={close}
						disabled={submitting}
						className="inline-flex items-center justify-center w-6 h-6 ml-3 text-gray-400 transition-colors duration-150 rounded dark:hover:text-gray-200 hover:text-gray-700"
						aria-label="close"
					>
						<i className="fas fa-times" />
					</button>
				</header>

				<div className="min-h-0 px-6 py-4 space-y-5 overflow-y-auto">
					<div className="grid gap-4 sm:grid-cols-2">
						<label className="block">
							<span className={labelClass}>Нэр</span>
							<input
								type="text"
								value={form.name}
								maxLength={80}
								onChange={(e) => set("name", e.target.value)}
								disabled={submitting}
								placeholder="Банкны 10-р сарын зар"
								autoFocus
								className={fieldClass}
							/>
						</label>
						<label className="block">
							<span className={labelClass}>Сурталчлагч</span>
							<input
								type="text"
								value={form.advertiser}
								maxLength={80}
								onChange={(e) => set("advertiser", e.target.value)}
								disabled={submitting}
								placeholder="Хоосон бол өөрсдийн зар"
								className={fieldClass}
							/>
						</label>
						<label className="block">
							<span className={labelClass}>Эхлэх өдөр</span>
							<input
								type="date"
								value={form.startDay}
								max={form.endDay || undefined}
								onChange={(e) => set("startDay", e.target.value)}
								disabled={submitting}
								className={fieldClass}
							/>
						</label>
						<label className="block">
							<span className={labelClass}>Дуусах өдөр</span>
							<input
								type="date"
								value={form.endDay}
								min={form.startDay || undefined}
								onChange={(e) => set("endDay", e.target.value)}
								disabled={submitting}
								className={fieldClass}
							/>
						</label>
					</div>
					<p className="-mt-3 text-xs text-gray-500 dark:text-gray-400">
						Хоёр өдөр хоёулаа орно. Хоосон бол асаасан үеэс эхэлж, унтраах хүртэл гарна. Киоск интернэтгүй үед ч
						хугацаандаа эхэлж, дуусна.
					</p>

					<fieldset>
						<legend className={labelClass}>Цагийн хуваарь</legend>
						<div className="flex flex-wrap gap-4 mb-2 text-sm text-gray-700 dark:text-gray-300">
							<label className="inline-flex items-center">
								<input
									type="radio"
									checked={allDay}
									onChange={() => setAllDay(true)}
									disabled={submitting}
									className={radioClass}
								/>
								<span className="ml-2">Өдөржин</span>
							</label>
							<label className="inline-flex items-center">
								<input
									type="radio"
									checked={!allDay}
									onChange={() => setAllDay(false)}
									disabled={submitting}
									className={radioClass}
								/>
								<span className="ml-2">Тодорхой цагт</span>
							</label>
						</div>
						{!allDay && (
							<div className="space-y-2">
								{form.hours.map((range, index) => (
									<div key={index} className="flex items-center gap-2">
										<input
											type="time"
											value={range.from}
											onChange={(e) => setHour(index, "from", e.target.value)}
											disabled={submitting}
											aria-label="Эхлэх цаг"
											className={timeClass}
										/>
										<span className="text-gray-400">–</span>
										<input
											type="time"
											value={range.to}
											onChange={(e) => setHour(index, "to", e.target.value)}
											disabled={submitting}
											aria-label="Дуусах цаг"
											className={timeClass}
										/>
										{form.hours.length > 1 && (
											<button
												type="button"
												className={removeButton}
												onClick={() => set("hours", form.hours.filter((_, i) => i !== index))}
												aria-label="Цагийн завсар хасах"
											>
												<i className="fas fa-times" />
											</button>
										)}
									</div>
								))}
								<button
									type="button"
									className={smallButton + (form.hours.length >= MAX_HOUR_RANGES ? " opacity-50 cursor-not-allowed" : "")}
									disabled={form.hours.length >= MAX_HOUR_RANGES}
									onClick={() => set("hours", [...form.hours, { from: "18:00", to: "22:00" }])}
								>
									<i className="mr-1.5 fas fa-plus" />
									Цаг нэмэх
								</button>
							</div>
						)}

						<div className="flex flex-wrap items-center gap-2 mt-3">
							{WEEKDAYS.map((day) => (
								<button
									key={day.value}
									type="button"
									onClick={() => toggleDay(day.value)}
									aria-pressed={form.days.includes(day.value)}
									disabled={submitting}
									className={chipClass(form.days.includes(day.value))}
								>
									{day.label}
								</button>
							))}
							<span className="mx-1 text-gray-300 dark:text-gray-600">|</span>
							<button
								type="button"
								onClick={() => set("days", ALL_DAYS)}
								className={"text-xs font-medium " + (sameDays(ALL_DAYS) ? "text-purple-600 dark:text-purple-300" : "text-gray-500 hover:underline")}
							>
								Өдөр бүр
							</button>
							<button
								type="button"
								onClick={() => set("days", WORKDAYS)}
								className={"text-xs font-medium " + (sameDays(WORKDAYS) ? "text-purple-600 dark:text-purple-300" : "text-gray-500 hover:underline")}
							>
								Ажлын өдөр
							</button>
						</div>
						<p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
							Улаанбаатарын цагаар. Киоск ирэх 7 хоногийн хуваариа урьдчилан авч, интернэтгүй үед ч барина. Зар
							солигдох нь 5 минут орчим хоцорч болно.
						</p>
					</fieldset>

					<label className="block">
						<span className={labelClass}>Давтамж</span>
						<select
							value={form.weight}
							onChange={(e) => set("weight", Number(e.target.value))}
							disabled={submitting}
							className={fieldClass + " sm:w-48"}
						>
							{WEIGHTS.map((weight) => (
								<option key={weight} value={weight}>
									{weightLabel(weight)}
								</option>
							))}
						</select>
						<span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
							Зарын нэг эргэлтэд бүтээл бүр хэдэн удаа гарах. Бусад зартай ээлжилж гарна. Киоск бүрт өөрөөр доорх
							жагсаалтаас тохируулна.
						</span>
					</label>

					<fieldset>
						<legend className={labelClass}>Киоскууд</legend>
						<div className="flex flex-wrap gap-4 mb-2 text-sm text-gray-700 dark:text-gray-300">
							<label className="inline-flex items-center">
								<input
									type="radio"
									checked={everywhere}
									onChange={() => setEverywhere(true)}
									disabled={submitting}
									className={radioClass}
								/>
								<span className="ml-2">Бүх киоск</span>
							</label>
							<label className="inline-flex items-center">
								<input
									type="radio"
									checked={!everywhere}
									onChange={() => setEverywhere(false)}
									disabled={submitting}
									className={radioClass}
								/>
								<span className="ml-2">Сонгосон киоскууд</span>
							</label>
						</div>
						{everywhere && (
							<p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
								Одоо байгаа {printers.length} киоск болон цаашид нэмэгдэх бүх киоскт гарна.
							</p>
						)}
						<div className="flex items-center gap-3 mb-2">
							<input
								type="search"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Киоск хайх"
								className={fieldClass}
							/>
							<span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
								{everywhere ? "Бүгд" : `${form.printers.length} сонгосон`}
								{ownWeights.length > 0 && ` · ${ownWeights.length} өөр давтамжтай`}
							</span>
						</div>
						{!everywhere && missing.length > 0 && (
							<p className="mb-2 text-xs text-yellow-700 dark:text-yellow-300">
								Сонгосон {missing.length} киоск системээс устгагдсан байна.
								<button
									type="button"
									onClick={() => set("printers", form.printers.filter((id) => !missing.includes(id)))}
									className="ml-2 font-semibold underline"
								>
									Жагсаалтаас хасах
								</button>
							</p>
						)}
						<div className="overflow-y-auto border border-gray-200 divide-y divide-gray-100 rounded-lg max-h-64 dark:border-gray-600 dark:divide-gray-700">
							{shown.length === 0 && (
								<p className="px-3 py-6 text-sm text-center text-gray-500 dark:text-gray-400">Киоск олдсонгүй.</p>
							)}
							{shown.map((printer) => {
								const delivery = deliveryOf(printer);
								const on = picked(printer._id);
								const own = form.weights[printer._id];
								return (
									<div
										key={printer._id}
										className={
											"flex items-center gap-3 px-3 py-2 " + (on && !everywhere ? "bg-purple-50 dark:bg-gray-700" : "")
										}
									>
										<label className={"flex items-center flex-1 min-w-0 " + (everywhere ? "" : "cursor-pointer")}>
											{!everywhere && (
												<input
													type="checkbox"
													checked={on}
													onChange={() => toggle(printer._id)}
													disabled={submitting}
													className="mr-3 text-purple-600 rounded focus:ring-purple-200 focus:ring-opacity-50 focus:ring"
												/>
											)}
											<span className="min-w-0">
												<span className="block text-sm font-medium text-gray-700 truncate dark:text-gray-200">
													{printer.name}
												</span>
												<span className="block text-xs text-gray-500 truncate dark:text-gray-400">
													{printer.location || "Байршилгүй"}
													{printer.delivery === "unsupported" && ` · ${delivery.label}`}
												</span>
											</span>
										</label>
										{on && (
											<select
												value={own === undefined || own === form.weight ? "" : own}
												onChange={(e) => setKioskWeight(printer._id, e.target.value)}
												disabled={submitting}
												aria-label={`${printer.name}-ийн давтамж`}
												className={
													"h-8 py-0 pl-2 pr-8 text-xs rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 " +
													(own !== undefined && own !== form.weight
														? "text-purple-700 font-semibold dark:text-purple-300"
														: "text-gray-500 dark:text-gray-400")
												}
											>
												<option value="">Үндсэн ({form.weight})</option>
												{WEIGHTS.filter((weight) => weight !== form.weight).map((weight) => (
													<option key={weight} value={weight}>
														{weightLabel(weight)}
													</option>
												))}
											</select>
										)}
									</div>
								);
							})}
						</div>
					</fieldset>

					<div className="flex items-start justify-between gap-4 p-3 border border-gray-200 rounded-lg dark:border-gray-600">
						<div>
							<p className="text-sm font-medium text-gray-700 dark:text-gray-200">Киоскуудад илгээх</p>
							<p className="text-xs text-gray-500 dark:text-gray-400">
								{isNew
									? "Бүтээлээ нэмж, шалгасны дараа асаахыг зөвлөж байна."
									: "Унтраавал киоскууд дараагийн heartbeat-ээр зарыг жагсаалтаасаа хасна."}
							</p>
						</div>
						<Toggle checked={form.enabled} onChange={(enabled) => set("enabled", enabled)} label="Киоскуудад илгээх" />
					</div>
				</div>

				<footer className="flex flex-wrap items-center justify-end flex-shrink-0 gap-3 px-6 py-3 border-t border-gray-100 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
					{/* A new form does not open by saying its name is missing */}
					{problem && (form.name.trim() || !isNew) && (
						<span className="mr-auto text-xs text-red-600 dark:text-red-400">
							<i className="mr-1.5 fas fa-exclamation-circle" />
							{problem}
						</span>
					)}
					<button type="button" onClick={close} disabled={submitting} className={secondaryButton}>
						Болих
					</button>
					<button
						type="submit"
						disabled={!canSubmit}
						className={primaryButton + (canSubmit ? "" : " opacity-50 cursor-not-allowed")}
					>
						{submitting ? "Хадгалж байна..." : isNew ? "Үүсгэх" : "Хадгалах"}
					</button>
				</footer>
			</form>
		</div>
	);
};

CampaignForm.propTypes = {
	campaign: PropTypes.object,
	printers: PropTypes.array.isRequired,
	onSaved: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	saveCampaign: PropTypes.func.isRequired,
};

export default connect(null, { saveCampaign })(CampaignForm);
